// BehaviouralGame.jsx - SJT Ranking Style
// Based on RIS-3751-TOM Situational Judgement Test format
// Players rank 4 responses from 1 (best) to 4 (worst)

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Play,
  Monitor,
  Smartphone,
  CheckCircle,
  RotateCcw,
  Brain,
  Shield,
  ArrowUp,
  ArrowDown,
  GripVertical,
  AlertTriangle,
  Info,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';

const GAME_STATES = {
  READY: 'ready',
  DEVICE_SELECT: 'device_select',
  INSTRUCTIONS: 'instructions',
  PLAYING: 'playing',
  FEEDBACK: 'feedback',
  FINISHED: 'finished',
};

const LEVEL_LABELS = {
  1: 'Customer service & professional conduct',
  2: 'Teamwork & communication',
  3: 'Safety & compliance',
  4: 'Problem-solving under pressure',
  5: 'Leadership & critical decisions',
};

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// SJT Scenarios with ideal rankings (1=best, 4=worst)
// Each option has an 'idealRank' indicating the expert ranking
const SJT_SCENARIOS = {
  1: [
    {
      situation: 'You are checking tickets on a train when a passenger asks you for directions to a connecting platform. Company policy states you must remain in your carriage during ticket inspections, but you should always assist passengers where possible.',
      options: [
        { id: 'a', text: 'Apologise and explain you cannot leave your carriage, but give clear verbal directions to the platform.', idealRank: 2 },
        { id: 'b', text: 'Apologise, explain you cannot leave, and ask the passenger to find another member of staff.', idealRank: 3 },
        { id: 'c', text: 'Apologise, explain the policy, and radio a colleague at the next station to meet and assist the passenger.', idealRank: 1 },
        { id: 'd', text: 'Leave your carriage briefly to walk the passenger to their platform.', idealRank: 4 },
      ],
    },
    {
      situation: 'A passenger complains loudly that the train is running 10 minutes late, becoming increasingly agitated. Other passengers are watching.',
      options: [
        { id: 'a', text: 'Apologise sincerely, explain the reason for the delay if known, and provide an estimated arrival time.', idealRank: 1 },
        { id: 'b', text: 'Tell the passenger there is nothing you can do about delays and ask them to be patient.', idealRank: 3 },
        { id: 'c', text: 'Ignore the passenger to avoid escalating the situation.', idealRank: 4 },
        { id: 'd', text: 'Apologise and offer to help them contact anyone who may be affected by their late arrival.', idealRank: 2 },
      ],
    },
    {
      situation: 'You notice your uniform has a small stain on it just before starting your shift. There is no time to change.',
      options: [
        { id: 'a', text: 'Report to your supervisor, explain the situation, and ask for guidance.', idealRank: 1 },
        { id: 'b', text: 'Wear the uniform anyway - the stain is small and likely unnoticeable.', idealRank: 3 },
        { id: 'c', text: 'Try to cover the stain and hope no one notices.', idealRank: 4 },
        { id: 'd', text: 'Ask a colleague if they have a spare item you could borrow with permission.', idealRank: 2 },
      ],
    },
    {
      situation: 'A colleague appears stressed and makes a minor error while following a procedure. You notice before it causes any problems.',
      options: [
        { id: 'a', text: 'Report the error immediately to your supervisor.', idealRank: 3 },
        { id: 'b', text: 'Speak privately to your colleague, check they are okay, and help them correct the error.', idealRank: 1 },
        { id: 'c', text: 'Ignore it - everyone makes mistakes and it was minor.', idealRank: 4 },
        { id: 'd', text: 'Correct the error yourself and mention it to your colleague later.', idealRank: 2 },
      ],
    },
    {
      situation: 'You are running five minutes late for your shift due to transport issues beyond your control.',
      options: [
        { id: 'a', text: 'Contact your supervisor immediately to inform them, then arrive as quickly and safely as possible.', idealRank: 1 },
        { id: 'b', text: 'Rush to work as fast as possible and explain when you arrive.', idealRank: 3 },
        { id: 'c', text: 'Send a quick message and skip your pre-shift checks to make up the time.', idealRank: 4 },
        { id: 'd', text: 'Contact your supervisor, inform them of the delay, and ask if any urgent tasks need covering.', idealRank: 2 },
      ],
    },
    {
      situation: 'A passenger approaches you asking for directions to a platform, but you are in the middle of an important safety check.',
      options: [
        { id: 'a', text: 'Politely ask them to wait a moment until you complete the safety check, then help them.', idealRank: 1 },
        { id: 'b', text: 'Ignore them and continue your check without acknowledging them.', idealRank: 4 },
        { id: 'c', text: 'Stop your check immediately to help them.', idealRank: 3 },
        { id: 'd', text: 'Point in the general direction while continuing your check.', idealRank: 2 },
      ],
    },
    {
      situation: 'You notice a colleague appears tired and mentions they did not sleep well. They are about to start driving duties.',
      options: [
        { id: 'a', text: 'Suggest they speak to their supervisor about their fitness for duty.', idealRank: 1 },
        { id: 'b', text: 'Say nothing as it is not your business.', idealRank: 4 },
        { id: 'c', text: 'Offer to cover part of their shift if possible.', idealRank: 2 },
        { id: 'd', text: 'Tell them to drink coffee and they will be fine.', idealRank: 3 },
      ],
    },
    {
      situation: 'During your break, you overhear two colleagues discussing a shortcut that bypasses a safety procedure.',
      options: [
        { id: 'a', text: 'Raise your concern with them directly about the safety implications.', idealRank: 1 },
        { id: 'b', text: 'Report them immediately to management without speaking to them.', idealRank: 3 },
        { id: 'c', text: 'Ignore it as it is their responsibility.', idealRank: 4 },
        { id: 'd', text: 'Mention it in passing that procedures exist for good reasons.', idealRank: 2 },
      ],
    },
  ],

  2: [
    {
      situation: 'During a shift handover, your colleague frequently interrupts you before you finish explaining important information.',
      options: [
        { id: 'a', text: 'Politely ask them to let you finish, explaining the importance of complete handovers for safety.', idealRank: 1 },
        { id: 'b', text: 'Interrupt them back to demonstrate how it feels.', idealRank: 4 },
        { id: 'c', text: 'Speak to your supervisor about the issue after the handover.', idealRank: 3 },
        { id: 'd', text: 'Write down all the information and hand it to them instead of verbal handover.', idealRank: 2 },
      ],
    },
    {
      situation: 'You receive conflicting instructions from two supervisors about how to complete a task.',
      options: [
        { id: 'a', text: 'Follow whichever instruction was given most recently.', idealRank: 3 },
        { id: 'b', text: 'Explain the situation to both supervisors and ask them to clarify which procedure to follow.', idealRank: 1 },
        { id: 'c', text: 'Use your own judgement and follow whichever seems safer.', idealRank: 2 },
        { id: 'd', text: 'Follow the instruction from your direct supervisor only.', idealRank: 4 },
      ],
    },
    {
      situation: 'A new team member has a different approach to completing tasks than the established team method.',
      options: [
        { id: 'a', text: 'Tell them they must conform to the team\'s established methods immediately.', idealRank: 4 },
        { id: 'b', text: 'Be open to their approach - they may have valuable ideas - while explaining team standards.', idealRank: 1 },
        { id: 'c', text: 'Ignore the differences and let them adapt naturally over time.', idealRank: 3 },
        { id: 'd', text: 'Suggest they discuss their approach with the supervisor for formal approval.', idealRank: 2 },
      ],
    },
    {
      situation: 'You disagree with a decision made by your team about how to handle a recurring issue.',
      options: [
        { id: 'a', text: 'Refuse to follow the decision until they consider your alternative.', idealRank: 4 },
        { id: 'b', text: 'Express your concerns professionally, then follow the decision while it\'s reviewed.', idealRank: 1 },
        { id: 'c', text: 'Follow the decision but complain to colleagues about it.', idealRank: 3 },
        { id: 'd', text: 'Follow the decision and document your concerns for the next review meeting.', idealRank: 2 },
      ],
    },
    {
      situation: 'During a team meeting, you notice a quieter colleague is being consistently talked over.',
      options: [
        { id: 'a', text: 'Say nothing - they should speak up for themselves.', idealRank: 4 },
        { id: 'b', text: 'Politely interject and ask for their input to give them space to contribute.', idealRank: 1 },
        { id: 'c', text: 'Mention it privately to the meeting chair afterwards.', idealRank: 2 },
        { id: 'd', text: 'Talk over others to show them how it feels.', idealRank: 3 },
      ],
    },
    {
      situation: 'A colleague from another department asks you to help with a task that falls outside your normal responsibilities.',
      options: [
        { id: 'a', text: 'Refuse politely, explaining it is not your responsibility.', idealRank: 4 },
        { id: 'b', text: 'Check with your supervisor if you have capacity to help, then assist if approved.', idealRank: 1 },
        { id: 'c', text: 'Help them immediately without checking, as teamwork is important.', idealRank: 3 },
        { id: 'd', text: 'Offer to help after completing your own priority tasks.', idealRank: 2 },
      ],
    },
    {
      situation: 'You notice that information shared in a team briefing contains an error that could affect operations.',
      options: [
        { id: 'a', text: 'Politely raise the error during the briefing so it can be corrected for everyone.', idealRank: 1 },
        { id: 'b', text: 'Wait until after the briefing and mention it privately to the person who gave it.', idealRank: 2 },
        { id: 'c', text: 'Assume someone else will notice and correct it.', idealRank: 4 },
        { id: 'd', text: 'Make a note and email the correct information to colleagues afterwards.', idealRank: 3 },
      ],
    },
    {
      situation: 'A colleague consistently takes longer breaks than allowed, leaving you to cover their duties.',
      options: [
        { id: 'a', text: 'Cover for them without saying anything to maintain harmony.', idealRank: 4 },
        { id: 'b', text: 'Speak to your colleague directly about the impact their extended breaks have.', idealRank: 1 },
        { id: 'c', text: 'Report them to management immediately.', idealRank: 3 },
        { id: 'd', text: 'Take equally long breaks yourself to make it fair.', idealRank: 4 },
      ],
    },
  ],

  3: [
    {
      situation: 'At a station, you notice an unattended bag that has been in the same location for over 30 minutes. Your supervisor is known for not taking security reports seriously.',
      options: [
        { id: 'a', text: 'Inform your supervisor and, for precaution, also discreetly inform another senior member of staff.', idealRank: 1 },
        { id: 'b', text: 'Inform your supervisor when other senior staff are present so they overhear.', idealRank: 2 },
        { id: 'c', text: 'Report directly to station security, bypassing your supervisor entirely.', idealRank: 3 },
        { id: 'd', text: 'Keep watching the bag and only report if someone doesn\'t collect it soon.', idealRank: 4 },
      ],
    },
    {
      situation: 'You feel unwell during your shift - slightly dizzy and nauseous. It is a busy day with staff shortages.',
      options: [
        { id: 'a', text: 'Continue working - the service depends on you and others are counting on you.', idealRank: 4 },
        { id: 'b', text: 'Report your condition to your supervisor immediately and follow their guidance.', idealRank: 1 },
        { id: 'c', text: 'Take a short break, have some water, and see if you feel better.', idealRank: 2 },
        { id: 'd', text: 'Ask a colleague to cover for you while you rest without telling your supervisor.', idealRank: 3 },
      ],
    },
    {
      situation: 'You notice a safety guard has been removed from a piece of equipment. A colleague says it\'s been like that for weeks and it\'s fine.',
      options: [
        { id: 'a', text: 'Report the missing guard immediately to your supervisor, regardless of what your colleague says.', idealRank: 1 },
        { id: 'b', text: 'If it\'s been weeks with no incidents, it\'s probably safe to continue.', idealRank: 4 },
        { id: 'c', text: 'Refuse to use the equipment until the guard is replaced and report it.', idealRank: 2 },
        { id: 'd', text: 'Ask your colleague to report it since they knew about it first.', idealRank: 3 },
      ],
    },
    {
      situation: 'You realise you may have made an error in a safety-critical procedure earlier in your shift, but you\'re not certain.',
      options: [
        { id: 'a', text: 'Report your concern to your supervisor immediately so it can be checked.', idealRank: 1 },
        { id: 'b', text: 'Go back and check the procedure yourself, then report if you confirm an error.', idealRank: 2 },
        { id: 'c', text: 'Wait and see if anyone notices a problem - you might be worrying over nothing.', idealRank: 4 },
        { id: 'd', text: 'Ask a trusted colleague for their opinion on whether you should report it.', idealRank: 3 },
      ],
    },
    {
      situation: 'You witness a colleague skip a mandatory safety check because they are running behind schedule.',
      options: [
        { id: 'a', text: 'Report the skipped check to your supervisor as a safety concern.', idealRank: 1 },
        { id: 'b', text: 'Speak privately to your colleague about the importance of the check.', idealRank: 2 },
        { id: 'c', text: 'Offer to help them catch up so they can complete the check.', idealRank: 3 },
        { id: 'd', text: 'Ignore it - they know the procedures and made their choice.', idealRank: 4 },
      ],
    },
    {
      situation: 'During a platform safety briefing, you notice a fire exit is partially blocked by equipment that was left there overnight.',
      options: [
        { id: 'a', text: 'Clear the obstruction immediately and report it to your supervisor.', idealRank: 1 },
        { id: 'b', text: 'Report the blocked exit to your supervisor and wait for instructions.', idealRank: 2 },
        { id: 'c', text: 'Make a note to mention it at the end of your shift.', idealRank: 4 },
        { id: 'd', text: 'Move the equipment yourself without reporting it.', idealRank: 3 },
      ],
    },
    {
      situation: 'You notice a passenger attempting to cross the tracks at an unauthorised location.',
      options: [
        { id: 'a', text: 'Shout to warn them immediately and alert the control room.', idealRank: 1 },
        { id: 'b', text: 'Press the emergency stop button first, then warn the passenger.', idealRank: 2 },
        { id: 'c', text: 'Run towards them to physically stop them crossing.', idealRank: 3 },
        { id: 'd', text: 'Wait to see if they cross safely before reporting.', idealRank: 4 },
      ],
    },
    {
      situation: 'A colleague tells you they have been asked to operate equipment they have not been trained on.',
      options: [
        { id: 'a', text: 'Advise them to speak to their supervisor about the training requirement immediately.', idealRank: 1 },
        { id: 'b', text: 'Offer to operate the equipment yourself if you are trained.', idealRank: 2 },
        { id: 'c', text: 'Tell them to just do their best and learn as they go.', idealRank: 4 },
        { id: 'd', text: 'Report the situation to management yourself.', idealRank: 3 },
      ],
    },
  ],

  4: [
    {
      situation: 'You are asked to attend an important conference in 2 days, but preparing the required materials normally takes 3 days. A colleague will cover your regular duties.',
      options: [
        { id: 'a', text: 'Explain the situation to your manager and ask if you could travel a day later once materials are ready.', idealRank: 1 },
        { id: 'b', text: 'Start immediately and take whatever materials you can prepare in time.', idealRank: 3 },
        { id: 'c', text: 'Ask your colleague for help preparing the materials to finish on time.', idealRank: 2 },
        { id: 'd', text: 'Explain to your manager that you cannot prepare adequate materials with such short notice.', idealRank: 4 },
      ],
    },
    {
      situation: 'A major incident occurs and you are asked to work significant overtime. You have personal commitments that evening.',
      options: [
        { id: 'a', text: 'Agree to the overtime - the incident takes priority and you can reschedule personal plans.', idealRank: 1 },
        { id: 'b', text: 'Explain your commitments and offer to work as long as possible before you must leave.', idealRank: 2 },
        { id: 'c', text: 'Refuse the overtime - your personal time is protected.', idealRank: 4 },
        { id: 'd', text: 'Agree reluctantly but express your frustration about the lack of notice.', idealRank: 3 },
      ],
    },
    {
      situation: 'You are given a new responsibility but not enough training to feel confident doing it safely.',
      options: [
        { id: 'a', text: 'Attempt the task carefully, learning as you go.', idealRank: 3 },
        { id: 'b', text: 'Request proper training before taking on the responsibility.', idealRank: 1 },
        { id: 'c', text: 'Ask an experienced colleague to supervise you until you feel confident.', idealRank: 2 },
        { id: 'd', text: 'Refuse the responsibility until formal training is provided.', idealRank: 4 },
      ],
    },
    {
      situation: 'Your team is significantly behind on an important project with a firm deadline. Cutting corners would save time but reduce quality.',
      options: [
        { id: 'a', text: 'Maintain quality standards and communicate honestly about the timeline.', idealRank: 1 },
        { id: 'b', text: 'Cut corners where it won\'t affect safety to meet the deadline.', idealRank: 3 },
        { id: 'c', text: 'Work longer hours as a team to maintain quality and meet the deadline.', idealRank: 2 },
        { id: 'd', text: 'Blame inadequate resources when the deadline is missed.', idealRank: 4 },
      ],
    },
    {
      situation: 'You discover that a process your team has followed for months doesn\'t comply with updated regulations.',
      options: [
        { id: 'a', text: 'Report the compliance issue immediately to your manager.', idealRank: 1 },
        { id: 'b', text: 'Research the new regulations fully before raising concerns.', idealRank: 2 },
        { id: 'c', text: 'Quietly start following the new regulations yourself.', idealRank: 3 },
        { id: 'd', text: 'Continue with the current process since it\'s been approved before.', idealRank: 4 },
      ],
    },
    {
      situation: 'You are managing a delayed service and passengers are becoming frustrated. You receive conflicting information about the estimated resumption time.',
      options: [
        { id: 'a', text: 'Communicate the most recent estimate to passengers while noting it may change.', idealRank: 1 },
        { id: 'b', text: 'Wait until you have confirmed information before making any announcements.', idealRank: 3 },
        { id: 'c', text: 'Give passengers the most optimistic estimate to keep them calm.', idealRank: 4 },
        { id: 'd', text: 'Explain that information is being verified and you will update them shortly.', idealRank: 2 },
      ],
    },
    {
      situation: 'During a busy period, you realise you won\'t be able to complete all your assigned tasks by the end of your shift.',
      options: [
        { id: 'a', text: 'Prioritise safety-critical tasks and inform your supervisor about remaining work.', idealRank: 1 },
        { id: 'b', text: 'Work through your break to try to complete everything.', idealRank: 3 },
        { id: 'c', text: 'Rush through tasks quickly to get them all done.', idealRank: 4 },
        { id: 'd', text: 'Complete what you can properly and hand over remaining tasks to the next shift.', idealRank: 2 },
      ],
    },
    {
      situation: 'A passenger with a valid complaint asks to speak to a manager, but no manager is currently available.',
      options: [
        { id: 'a', text: 'Take their details, document the complaint thoroughly, and arrange a manager callback.', idealRank: 1 },
        { id: 'b', text: 'Apologise and try to resolve the issue yourself within your authority.', idealRank: 2 },
        { id: 'c', text: 'Tell them to contact customer services online.', idealRank: 3 },
        { id: 'd', text: 'Explain that managers are busy and suggest they return another day.', idealRank: 4 },
      ],
    },
  ],

  5: [
    {
      situation: 'As a team leader, you discover that a respected team member has been consistently falsifying minor records to meet targets.',
      options: [
        { id: 'a', text: 'Report the issue to HR/management immediately as it\'s a serious integrity matter.', idealRank: 1 },
        { id: 'b', text: 'Speak privately to the team member first, then report regardless of their response.', idealRank: 2 },
        { id: 'c', text: 'Give them a warning and monitor more closely before deciding whether to report.', idealRank: 3 },
        { id: 'd', text: 'Consider the context - if targets are unrealistic, address that issue instead.', idealRank: 4 },
      ],
    },
    {
      situation: 'During an emergency, you must decide whether to follow standard procedure (which is slower) or use your judgement for a faster response.',
      options: [
        { id: 'a', text: 'Follow standard procedure - it exists for good reason.', idealRank: 2 },
        { id: 'b', text: 'Use your judgement if you\'re confident the faster approach is safe.', idealRank: 3 },
        { id: 'c', text: 'Quickly consult with available colleagues before deciding.', idealRank: 1 },
        { id: 'd', text: 'Take the fastest action regardless of procedure - time is critical.', idealRank: 4 },
      ],
    },
    {
      situation: 'A senior colleague asks you to approve their work when you have concerns about its quality.',
      options: [
        { id: 'a', text: 'Approve it to maintain the relationship - they are more experienced.', idealRank: 4 },
        { id: 'b', text: 'Explain your specific concerns professionally and request revisions.', idealRank: 1 },
        { id: 'c', text: 'Escalate to management rather than confronting them directly.', idealRank: 3 },
        { id: 'd', text: 'Approve with documented reservations for the record.', idealRank: 2 },
      ],
    },
    {
      situation: 'You are asked to lead an important project but know a colleague has more relevant expertise.',
      options: [
        { id: 'a', text: 'Accept the role and ask the colleague to support you with their expertise.', idealRank: 1 },
        { id: 'b', text: 'Suggest your colleague leads instead as they\'re better qualified.', idealRank: 3 },
        { id: 'c', text: 'Accept the role confidently - you were selected for a reason.', idealRank: 2 },
        { id: 'd', text: 'Decline and recommend your colleague take the lead.', idealRank: 4 },
      ],
    },
    {
      situation: 'As a supervisor, you must choose between two qualified candidates for a promotion. One is a close friend.',
      options: [
        { id: 'a', text: 'Assess both purely on merit and documented performance.', idealRank: 1 },
        { id: 'b', text: 'Recuse yourself from the decision due to the conflict of interest.', idealRank: 2 },
        { id: 'c', text: 'Be slightly harder on your friend to prove you\'re being fair.', idealRank: 3 },
        { id: 'd', text: 'Trust that you can be objective despite the friendship.', idealRank: 4 },
      ],
    },
    {
      situation: 'You become aware that a company policy is being widely ignored across multiple teams because it is seen as impractical.',
      options: [
        { id: 'a', text: 'Follow the policy yourself and formally raise concerns through proper channels.', idealRank: 1 },
        { id: 'b', text: 'Discuss with other supervisors to build consensus before escalating.', idealRank: 2 },
        { id: 'c', text: 'Ignore it like everyone else - the policy clearly needs changing.', idealRank: 4 },
        { id: 'd', text: 'Enforce the policy strictly within your own team regardless.', idealRank: 3 },
      ],
    },
    {
      situation: 'A safety audit reveals minor issues in your area of responsibility. Your manager asks you to resolve them quickly before the next inspection.',
      options: [
        { id: 'a', text: 'Address the issues properly even if it takes longer than the deadline allows.', idealRank: 1 },
        { id: 'b', text: 'Fix the visible issues and document a plan for thorough follow-up.', idealRank: 2 },
        { id: 'c', text: 'Apply quick fixes to pass the inspection and address root causes later.', idealRank: 3 },
        { id: 'd', text: 'Focus only on the issues most likely to be re-inspected.', idealRank: 4 },
      ],
    },
    {
      situation: 'You are asked to implement a cost-saving measure that you believe will compromise service quality.',
      options: [
        { id: 'a', text: 'Present your concerns formally with evidence of potential impacts before implementation.', idealRank: 1 },
        { id: 'b', text: 'Implement the measure while monitoring and documenting any negative effects.', idealRank: 2 },
        { id: 'c', text: 'Refuse to implement until your concerns are addressed.', idealRank: 4 },
        { id: 'd', text: 'Implement it as instructed - management has more information than you.', idealRank: 3 },
      ],
    },
  ],
};

const DeviceModeSelector = ({ onSelect }) => (
  <Card className="overflow-hidden border-slate-200 shadow-sm">
    <CardContent className="p-5 sm:p-8">
      <div className="mx-auto mb-6 max-w-xl text-center">
        <Badge className="mb-3 bg-[#0B1D51]/10 text-[#0B1D51] hover:bg-[#0B1D51]/10">
          Setup
        </Badge>
        <h2 className="font-heading text-2xl font-bold tracking-tight text-[#0B1D51]">
          Select your device
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-[#64748B]">
          This helps tailor the layout for your device.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Button
          type="button"
          variant="outline"
          className="group h-auto min-h-36 flex-col gap-3 rounded-2xl border-2 border-slate-200 p-6 text-center hover:border-[#0B1D51] hover:bg-[#0B1D51]/5"
          onClick={() => onSelect('desktop')}
          data-testid="select-desktop"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0B1D51]/10 transition group-hover:bg-[#0B1D51]/15">
            <Monitor className="h-8 w-8 text-[#0B1D51]" />
          </span>
          <span>
            <span className="block font-heading text-base font-bold text-[#0B1D51]">
              Laptop/Desktop
            </span>
            <span className="mt-1 block text-xs font-normal text-[#64748B]">
              Larger layout with pointer controls
            </span>
          </span>
        </Button>

        <Button
          type="button"
          variant="outline"
          className="group h-auto min-h-36 flex-col gap-3 rounded-2xl border-2 border-slate-200 p-6 text-center hover:border-[#0B1D51] hover:bg-[#0B1D51]/5"
          onClick={() => onSelect('mobile')}
          data-testid="select-mobile"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0B1D51]/10 transition group-hover:bg-[#0B1D51]/15">
            <Smartphone className="h-8 w-8 text-[#0B1D51]" />
          </span>
          <span>
            <span className="block font-heading text-base font-bold text-[#0B1D51]">
              Tablet/Phone
            </span>
            <span className="mt-1 block text-xs font-normal text-[#64748B]">
              Touch-friendly layout
            </span>
          </span>
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Ranking Option Component - allows moving up/down
const RankingOption = ({ option, rank, totalOptions, onMoveUp, onMoveDown, disabled }) => (
  <div 
    className={`flex items-center gap-3 p-3 sm:p-4 rounded-lg border-2 transition-all ${
      disabled ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200 hover:border-[#0B1D51]/30'
    }`}
  >
    {/* Rank Number */}
    <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold text-lg ${
      rank === 1 ? 'bg-[#2CB67D] text-white' :
      rank === 2 ? 'bg-[#0B1D51] text-white' :
      rank === 3 ? 'bg-[#FF9F1C] text-white' :
      'bg-slate-400 text-white'
    }`}>
      {rank}
    </div>

    {/* Option Text */}
    <div className="flex-grow">
      <span className="text-sm sm:text-base text-[#0F172A]">{option.text}</span>
    </div>

    {/* Move Buttons */}
    {!disabled && (
      <div className="flex flex-col gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onMoveUp}
          disabled={rank === 1}
          className="h-8 w-8 p-0 hover:bg-[#0B1D51]/10"
          data-testid={`move-up-${option.id}`}
        >
          <ArrowUp className={`h-4 w-4 ${rank === 1 ? 'text-slate-300' : 'text-[#0B1D51]'}`} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onMoveDown}
          disabled={rank === totalOptions}
          className="h-8 w-8 p-0 hover:bg-[#0B1D51]/10"
          data-testid={`move-down-${option.id}`}
        >
          <ArrowDown className={`h-4 w-4 ${rank === totalOptions ? 'text-slate-300' : 'text-[#0B1D51]'}`} />
        </Button>
      </div>
    )}
  </div>
);

const BehaviouralGame = ({ level = 1, onComplete }) => {
  const [gameState, setGameState] = useState(GAME_STATES.DEVICE_SELECT);
  const [deviceMode, setDeviceMode] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [rankedOptions, setRankedOptions] = useState([]);
  const [questionResults, setQuestionResults] = useState([]);
  const [totalScore, setTotalScore] = useState(0);
  const startTimeRef = useRef(null);

  // Prepare questions for this level
  const preparedQuestions = useMemo(() => {
    const levelScenarios = SJT_SCENARIOS[level] || SJT_SCENARIOS[1];
    const shuffled = shuffleArray(levelScenarios);
    return shuffled.slice(0, 5); // 5 questions per level
  }, [level]);

  const currentQuestion = preparedQuestions[currentQuestionIndex];

  // Initialize ranked options when question changes
  useEffect(() => {
    if (currentQuestion && gameState === GAME_STATES.PLAYING) {
      // Shuffle options for display (so ideal order isn't obvious)
      const shuffledOptions = shuffleArray([...currentQuestion.options]);
      setRankedOptions(shuffledOptions);
    }
  }, [currentQuestionIndex, currentQuestion, gameState]);

  const handleDeviceSelect = (mode) => {
    setDeviceMode(mode);
    setGameState(GAME_STATES.INSTRUCTIONS);
  };

  const startGame = () => {
    startTimeRef.current = Date.now();
    setCurrentQuestionIndex(0);
    setQuestionResults([]);
    setTotalScore(0);
    setGameState(GAME_STATES.PLAYING);
  };

  const moveOption = (fromIndex, direction) => {
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= rankedOptions.length) return;

    const newRanked = [...rankedOptions];
    [newRanked[fromIndex], newRanked[toIndex]] = [newRanked[toIndex], newRanked[fromIndex]];
    setRankedOptions(newRanked);
  };

  const calculateScore = (userRanking) => {
    // Calculate score based on how close user ranking is to ideal ranking
    // Perfect match = 4 points per option = 16 max
    // 1 position off = 3 points, 2 off = 2 points, 3 off = 1 point
    let score = 0;
    userRanking.forEach((option, index) => {
      const userRank = index + 1;
      const idealRank = option.idealRank;
      const difference = Math.abs(userRank - idealRank);
      
      if (difference === 0) score += 4;
      else if (difference === 1) score += 3;
      else if (difference === 2) score += 2;
      else score += 1;
    });
    return score; // Max 16 per question
  };

  const handleSubmitRanking = () => {
    const score = calculateScore(rankedOptions);
    const maxScore = 16;
    
    const result = {
      question: currentQuestion.situation,
      userRanking: rankedOptions.map((opt, idx) => ({ ...opt, userRank: idx + 1 })),
      score,
      maxScore,
    };
    
    setQuestionResults([...questionResults, result]);
    setTotalScore(totalScore + score);
    setGameState(GAME_STATES.FEEDBACK);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex + 1 >= preparedQuestions.length) {
      // Game complete
      const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
      const maxPossible = preparedQuestions.length * 16;
      
      setGameState(GAME_STATES.FINISHED);
      
      if (onComplete) {
        const finalScore = Math.round(totalScore + (questionResults[questionResults.length - 1]?.score ?? 0));
        onComplete({
          score: finalScore,
          maxScore: Math.round(maxPossible),
          accuracy: maxPossible > 0 ? Math.round((finalScore / maxPossible) * 100) : 0,
          timeSpent,
          questionsAnswered: preparedQuestions.length,
        });
      }
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setGameState(GAME_STATES.PLAYING);
    }
  };

  const handleRestart = () => {
    setCurrentQuestionIndex(0);
    setQuestionResults([]);
    setTotalScore(0);
    setGameState(GAME_STATES.INSTRUCTIONS);
  };

  // Device Selection Screen
  if (gameState === GAME_STATES.DEVICE_SELECT) {
    return <DeviceModeSelector onSelect={handleDeviceSelect} />;
  }

  // Instructions Screen
  if (gameState === GAME_STATES.INSTRUCTIONS) {
    return (
      <Card className="border-slate-200">
        <CardContent className="pt-8 pb-6 px-4 sm:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <Badge className="mb-3 bg-[#FF9F1C]/10 text-[#9A5A00] hover:bg-[#FF9F1C]/10">
              Level {level}: {LEVEL_LABELS[level] || 'Situational Judgement'}
            </Badge>

            <h2 className="font-heading text-2xl font-bold tracking-tight text-[#0B1D51] sm:text-3xl">
              Situational Judgement Test
            </h2>
            
            <p className="text-xs text-[#FF9F1C] font-medium mt-2 mb-6">
              Develops skills to improve performance on the Situational Judgement Exercise (SJE)
            </p>

            {/* Instructions */}
            <div className="bg-[#F8FAFC] rounded-lg p-4 sm:p-6 text-left mb-6">
              <h3 className="font-semibold text-[#0B1D51] mb-4 flex items-center gap-2">
                <Info className="w-5 h-5" />
                How This Test Works
              </h3>
              
              <ol className="space-y-3 text-sm text-[#64748B]">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0B1D51] text-white flex items-center justify-center text-xs font-bold">1</span>
                  <span>You will read a workplace situation described.</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0B1D51] text-white flex items-center justify-center text-xs font-bold">2</span>
                  <span>Below each situation are <strong>four response options</strong> (A, B, C, D).</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0B1D51] text-white flex items-center justify-center text-xs font-bold">3</span>
                  <span><strong>Rank these options</strong> from your 1st choice (best response) to your 4th choice (worst response).</span>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0B1D51] text-white flex items-center justify-center text-xs font-bold">4</span>
                  <span>Use the <strong>arrow buttons</strong> to move options up or down in your ranking.</span>
                </li>
              </ol>

              <div className="mt-4 pt-4 border-t border-slate-200">
                <h4 className="font-semibold text-[#0B1D51] mb-2">Scoring:</h4>
                <p className="text-sm text-[#64748B]">
                  Points are awarded based on how closely your ranking matches the ideal professional response. 
                  There is no single "correct" answer, but some responses are generally considered more appropriate than others.
                </p>
              </div>

              <div className="mt-4 flex items-start gap-2 p-3 bg-amber-50 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  <strong>RIS-3751-TOM:</strong> This exercise develops skills assessed by the Situational Judgement Exercise (SJE) - 
                  measuring your ability to identify appropriate professional responses in workplace scenarios.
                </p>
              </div>
            </div>

            <Button 
              onClick={startGame}
              size="lg"
              className="bg-[#0B1D51] hover:bg-[#0B1D51]/90"
              data-testid="start-game-btn"
            >
              <Play className="w-5 h-5 mr-2" />
              Begin Test ({preparedQuestions.length} scenarios)
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Playing Screen - Ranking Interface
  if (gameState === GAME_STATES.PLAYING && currentQuestion) {
    const progress = ((currentQuestionIndex) / preparedQuestions.length) * 100;

    return (
      <Card className="border-slate-200">
        <CardContent className="pt-6 pb-6 px-4 sm:px-6">
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-[#0B1D51]">
                Scenario {currentQuestionIndex + 1} of {preparedQuestions.length}
              </span>
              <Badge variant="outline">{LEVEL_LABELS[level]}</Badge>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Situation */}
          <div className="bg-[#0B1D51]/5 rounded-lg p-4 sm:p-5 mb-6">
            <h3 className="font-semibold text-[#0B1D51] mb-2 text-sm uppercase tracking-wide">Situation:</h3>
            <p className="text-[#0F172A] text-sm sm:text-base leading-relaxed">
              {currentQuestion.situation}
            </p>
          </div>

          {/* Ranking Instructions */}
          <div className="mb-4">
            <h3 className="font-semibold text-[#0B1D51] mb-1">
              Rank the responses from best (1) to worst (4):
            </h3>
            <p className="text-sm text-[#64748B]">
              Use the arrows to reorder. Position 1 = your best choice.
            </p>
          </div>

          {/* Ranking Options */}
          <div className="space-y-3 mb-6">
            {rankedOptions.map((option, index) => (
              <RankingOption
                key={option.id}
                option={option}
                rank={index + 1}
                totalOptions={rankedOptions.length}
                onMoveUp={() => moveOption(index, 'up')}
                onMoveDown={() => moveOption(index, 'down')}
                disabled={false}
              />
            ))}
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmitRanking}
            className="w-full bg-[#2CB67D] hover:bg-[#2CB67D]/90"
            size="lg"
            data-testid="submit-ranking-btn"
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            Confirm Ranking
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Feedback Screen
  if (gameState === GAME_STATES.FEEDBACK) {
    const lastResult = questionResults[questionResults.length - 1];
    const percentScore = Math.round((lastResult.score / lastResult.maxScore) * 100);

    return (
      <Card className="border-slate-200">
        <CardContent className="pt-6 pb-6 px-4 sm:px-6">
          <div className="text-center mb-6">
            <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
              percentScore >= 75 ? 'bg-[#2CB67D]/10' : percentScore >= 50 ? 'bg-[#FF9F1C]/10' : 'bg-red-100'
            }`}>
              {percentScore >= 75 ? (
                <CheckCircle className="w-8 h-8 text-[#2CB67D]" />
              ) : percentScore >= 50 ? (
                <Brain className="w-8 h-8 text-[#FF9F1C]" />
              ) : (
                <AlertTriangle className="w-8 h-8 text-red-500" />
              )}
            </div>
            <h3 className="font-heading text-xl font-bold text-[#0B1D51] mb-1">
              {percentScore >= 75 ? 'Excellent Judgement!' : percentScore >= 50 ? 'Good Attempt' : 'Room for Improvement'}
            </h3>
            <p className="text-[#64748B]">
              You scored <strong>{lastResult.score}</strong> out of <strong>{lastResult.maxScore}</strong> points
            </p>
          </div>

          {/* Show ideal ranking comparison */}
          <div className="bg-[#F8FAFC] rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-[#0B1D51] mb-3">Ideal Ranking Comparison:</h4>
            <div className="space-y-2">
              {lastResult.userRanking
                .sort((a, b) => a.idealRank - b.idealRank)
                .map((option) => (
                  <div key={option.id} className="flex items-center gap-3 text-sm">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                      option.userRank === option.idealRank ? 'bg-[#2CB67D]' :
                      Math.abs(option.userRank - option.idealRank) === 1 ? 'bg-[#FF9F1C]' : 'bg-red-400'
                    }`}>
                      {option.idealRank}
                    </span>
                    <span className="flex-grow text-[#0F172A]">{option.text}</span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      option.userRank === option.idealRank ? 'bg-[#2CB67D]/10 text-[#2CB67D]' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      You: {option.userRank}
                    </span>
                  </div>
                ))}
            </div>
          </div>

          <Button
            onClick={handleNextQuestion}
            className="w-full bg-[#0B1D51] hover:bg-[#0B1D51]/90"
            size="lg"
            data-testid="next-question-btn"
          >
            {currentQuestionIndex + 1 >= preparedQuestions.length ? 'View Results' : 'Next Scenario'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Finished Screen
  if (gameState === GAME_STATES.FINISHED) {
    const finalScore = questionResults.reduce((sum, r) => sum + r.score, 0);
    const maxPossible = preparedQuestions.length * 16;
    const percentage = Math.round((finalScore / maxPossible) * 100);

    return (
      <Card className="border-slate-200">
        <CardContent className="pt-8 pb-6 px-4 sm:px-8">
          <div className="text-center max-w-md mx-auto">
            <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${
              percentage >= 75 ? 'bg-[#2CB67D]/10' : percentage >= 50 ? 'bg-[#FF9F1C]/10' : 'bg-red-100'
            }`}>
              {percentage >= 75 ? (
                <Shield className="w-10 h-10 text-[#2CB67D]" />
              ) : (
                <Brain className="w-10 h-10 text-[#FF9F1C]" />
              )}
            </div>

            <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
              Assessment Complete
            </h2>

            <div className="text-4xl font-bold text-[#0B1D51] mb-2">
              {percentage}%
            </div>

            <p className="text-[#64748B] mb-6">
              Final Score: {finalScore} / {maxPossible} points
            </p>

            {/* Performance interpretation */}
            <div className={`p-4 rounded-lg mb-6 ${
              percentage >= 75 ? 'bg-[#2CB67D]/10' : percentage >= 50 ? 'bg-[#FF9F1C]/10' : 'bg-red-50'
            }`}>
              <p className={`text-sm ${
                percentage >= 75 ? 'text-[#2CB67D]' : percentage >= 50 ? 'text-[#9A5A00]' : 'text-red-700'
              }`}>
                {percentage >= 75 ? (
                  'Excellent! Your responses demonstrate strong professional judgement aligned with railway industry expectations.'
                ) : percentage >= 50 ? (
                  'Good effort. Review the feedback for each scenario to understand how rankings could be improved.'
                ) : (
                  'This area needs more practice. Focus on safety-first thinking and following proper procedures.'
                )}
              </p>
            </div>

            <div className="space-y-3">
              <Button
                onClick={handleRestart}
                variant="outline"
                className="w-full"
                data-testid="restart-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default BehaviouralGame;
